import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { auth } from "../../firebase";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { usePlaceCheckInStatus } from "../../hooks/usePlaceCheckInStatus";
import { useUserProfile } from "../../hooks/useUserProfile";
import { buildFeedbackMailTo } from "../../lib/build-feedback-mail-to";

export function useUserSessionLinks() {
  const { user, isUserAdmin } = useAppAuthState();
  const { profile } = useUserProfile(user?.uid);
  const { checkedInPlaceId, checkedInPlaceName } = usePlaceCheckInStatus(
    null,
    user?.uid
  );

  const handleLogout = useCallback(async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, []);

  return useMemo(() => {
    if (!user) {
      const links = [
        <li className="nav-item" key="login-link">
          <Link
            to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
            className="nav-link flash-container login-link"
          >
            Log In
          </Link>
        </li>,
      ];
      return { links, moreDropdownLinks: [] };
    }

    const moreDropdownLinks = [];
    const links = [];

    if (user) {
      const mailto = buildFeedbackMailTo(user);
      moreDropdownLinks.push(
        <li key="feedback-link">
          <a href={mailto} className="dropdown-item">
            Feedback
          </a>
        </li>
      );
    }

    if (isUserAdmin) {
      moreDropdownLinks.push(
        <li key="admin-divider">
          <hr className="dropdown-divider" />
        </li>
      );
      moreDropdownLinks.push(
        <li key="admin-header">
          <h6 className="dropdown-header">Admin</h6>
        </li>
      );
      moreDropdownLinks.push(
        <li key="admin-link">
          <Link to="/admin" className="dropdown-item">
            Admin
          </Link>
        </li>
      );
      moreDropdownLinks.push(
        <li key="admin-link-users">
          <Link to="/admin/users" className="dropdown-item">
            Admin Users
          </Link>
        </li>
      );
      moreDropdownLinks.push(
        <li key="admin-link-places">
          <Link to="/admin/places" className="dropdown-item">
            Admin Places
          </Link>
        </li>
      );
    }

    if (checkedInPlaceId && checkedInPlaceName) {
      links.push(
        <li
          className="nav-item checked-in-place-link"
          key="checked-in-place-link"
        >
          <Link
            to={`/place/${checkedInPlaceId}`}
            className="nav-link icon-nav-link"
          >
            <div>
              {checkedInPlaceName}
              <i className="material-symbols-outlined">counter_8</i>
            </div>
          </Link>
        </li>
      );
    }

    if (profile) {
      const photoURL = profile.photoURL;
      links.push(
        <li className="nav-item profile-nav-item" key="profile-link">
          <Link to="/profile" className="nav-link icon-nav-link">
            <div style={{ display: "flex", alignItems: "center" }}>
              {photoURL && (
                <img src={photoURL} alt="Profile" className="profile-photo" />
              )}
              <span>{profile?.username ?? user.email}</span>
              <i
                title="Log out"
                className="material-icons"
                onClick={handleLogout}
                style={{ marginLeft: "auto", cursor: "pointer" }}
              >
                close
              </i>
            </div>
          </Link>
        </li>
      );
    }

    return { links, moreDropdownLinks };
  }, [
    user,
    isUserAdmin,
    checkedInPlaceId,
    checkedInPlaceName,
    profile,
    handleLogout,
  ]);
}
